import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { gigSwal } from '../utils/swal';
import { ScrollText, Music, ChevronUp, ChevronDown, X, Mic, ExternalLink, Check, Bookmark } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_API_URL || '';

function SetListBuilder() {
  const theme = useTheme();
  const { gigId } = useParams();
  const navigate = useNavigate();

  const [gig, setGig] = useState(null);
  const [setList, setSetList] = useState({});
  const [allSongs, setAllSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSet, setCurrentSet] = useState(1);
  const [venueHistory, setVenueHistory] = useState([]);
  const [venueSongs, setVenueSongs] = useState([]);
  const [showVenueHistory, setShowVenueHistory] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('setlist');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedSongIds, setSelectedSongIds] = useState(new Set());
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [previousGig, setPreviousGig] = useState(null);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [loadingTemplateId, setLoadingTemplateId] = useState(null);
  const [copyingLastGig, setCopyingLastGig] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchGig();
    fetchSetList();
    fetchAllSongs();
  }, [gigId]);

  const fetchGig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gigs`);
      const foundGig = response.data.find(g => g.id === parseInt(gigId));
      setGig(foundGig);
      if (foundGig?.venue_name) fetchVenueHistory(foundGig.venue_name);
    } catch (err) {
      console.error('Error fetching gig:', err);
      toast.error('Failed to load gig');
    }
  };

  const fetchSetList = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gigs/${gigId}/setlist`);
      setSetList(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching set list:', err);
      setLoading(false);
    }
  };

  const fetchAllSongs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/songs`);
      setAllSongs(response.data.filter(s => s.is_active));
    } catch (err) {
      console.error('Error fetching songs:', err);
    }
  };

  const fetchVenueHistory = async (venueName) => {
    try {
      const [historyRes, songsRes] = await Promise.all([
        axios.get(`${API_URL}/api/venues/${encodeURIComponent(venueName)}/history`),
        axios.get(`${API_URL}/api/venues/${encodeURIComponent(venueName)}/songs`),
      ]);
      setVenueHistory(historyRes.data);
      setVenueSongs(songsRes.data);
    } catch (err) {
      console.error('Error fetching venue history:', err);
    }
  };

  const addRequestBlock = async () => {
    try {
      const currentSongs = setList[currentSet] || [];
      await axios.post(`${API_URL}/api/gigs/${gigId}/setlist`, {
        set_number: currentSet,
        slot_type: 'request_block',
        duration_minutes: 20,
        position: currentSongs.length + 1,
      });
      fetchSetList();
      toast.success('Request block added');
      if (isMobile) setActiveTab('setlist');
    } catch (err) {
      console.error('Error adding request block:', err);
      toast.error('Failed to add request block');
    }
  };

  const toggleSongSelection = (songId) => {
    setSelectedSongIds(prev => {
      const next = new Set(prev);
      if (next.has(songId)) next.delete(songId);
      else next.add(songId);
      return next;
    });
  };

  const addSelectedSongs = async () => {
    if (selectedSongIds.size === 0) return;
    const currentSongs = setList[currentSet] || [];
    const startPosition = currentSongs.length + 1;
    const ids = [...selectedSongIds];
    try {
      await Promise.all(
        ids.map((songId, idx) =>
          axios.post(`${API_URL}/api/gigs/${gigId}/setlist`, {
            set_number: currentSet,
            song_id: songId,
            position: startPosition + idx,
          })
        )
      );
      fetchSetList();
      setSelectedSongIds(new Set());
      toast.success(`${ids.length} song${ids.length !== 1 ? 's' : ''} added to set`);
      if (isMobile) setActiveTab('setlist');
    } catch (err) {
      console.error('Error adding songs:', err);
      toast.error('Failed to add songs');
    }
  };

  const closeTemplatesSheet = () => { setTemplatesOpen(false); setShowSaveInput(false); setSaveTemplateName(''); };

  const openTemplatesSheet = async () => {
    const [tmplRes, prevRes] = await Promise.all([
      axios.get(`${API_URL}/api/templates`),
      axios.get(`${API_URL}/api/gigs/${gigId}/setlist/previous-gig`),
    ]);
    setTemplates(tmplRes.data);
    setPreviousGig(prevRes.data);
    setTemplatesOpen(true);
  };

  const saveTemplate = async () => {
    const name = saveTemplateName.trim();
    if (!name) return;
    setSavingTemplate(true);
    try {
      const res = await axios.post(`${API_URL}/api/templates`, { name, gig_id: gigId });
      setTemplates(prev => [res.data, ...prev]);
      setSaveTemplateName('');
      setShowSaveInput(false);
      toast.success(`Template "${name}" saved`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const loadTemplate = async (template) => {
    const hasExisting = Object.values(setList).some(s => s.length > 0);
    if (hasExisting) {
      const result = await gigSwal(theme, {
        title: 'Replace set list?',
        text: `This will clear the current set list and load "${template.name}".`,
        icon: 'warning', showCancelButton: true,
        confirmButtonColor: theme.primary, confirmButtonText: 'Yes, load it', reverseButtons: true,
      });
      if (!result.isConfirmed) return;
    }
    setLoadingTemplateId(template.id);
    try {
      await axios.post(`${API_URL}/api/gigs/${gigId}/setlist/load-template/${template.id}`);
      await fetchSetList();
      closeTemplatesSheet();
      toast.success(`Loaded "${template.name}"`);
    } catch {
      toast.error('Failed to load template');
    } finally {
      setLoadingTemplateId(null);
    }
  };

  const deleteTemplate = async (templateId, name) => {
    const result = await gigSwal(theme, {
      title: 'Delete template?',
      text: `"${name}" will be permanently deleted.`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#dc3545', confirmButtonText: 'Delete', reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`${API_URL}/api/templates/${templateId}`);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const copyLastGig = async () => {
    if (!previousGig) return;
    const hasExisting = Object.values(setList).some(s => s.length > 0);
    if (hasExisting) {
      const result = await gigSwal(theme, {
        title: 'Replace set list?',
        text: `This will clear the current set list and copy ${previousGig.song_count} songs from "${previousGig.gig_name}".`,
        icon: 'warning', showCancelButton: true,
        confirmButtonColor: theme.primary, confirmButtonText: 'Yes, copy it', reverseButtons: true,
      });
      if (!result.isConfirmed) return;
    }
    setCopyingLastGig(true);
    try {
      await axios.post(`${API_URL}/api/gigs/${gigId}/setlist/copy-from-gig/${previousGig.id}`);
      await fetchSetList();
      closeTemplatesSheet();
      toast.success(`Copied ${previousGig.song_count} songs from "${previousGig.gig_name}"`);
    } catch {
      toast.error('Failed to copy set list');
    } finally {
      setCopyingLastGig(false);
    }
  };

  const updateBlockDuration = async (setListId, durationMinutes) => {
    try {
      await axios.patch(`${API_URL}/api/setlist/${setListId}`, { duration_minutes: durationMinutes });
      fetchSetList();
    } catch (err) {
      console.error('Error updating block duration:', err);
      toast.error('Failed to update duration');
    }
  };

  const removeSlot = async (setListId, slotType) => {
    const isBlock = slotType === 'request_block';
    const result = await gigSwal(theme, {
      title: isBlock ? 'Remove Request Block?' : 'Remove Song?',
      text: isBlock ? 'Remove this request block from the set list?' : 'Remove this song from the set list?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Yes, remove it',
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`${API_URL}/api/setlist/${setListId}`);
      fetchSetList();
      toast.success(isBlock ? 'Request block removed' : 'Song removed');
    } catch (err) {
      console.error('Error removing slot:', err);
      toast.error('Failed to remove');
    }
  };

  const moveSong = async (setListId, direction) => {
    const currentSongs = setList[currentSet] || [];
    const songIndex = currentSongs.findIndex(s => s.id === setListId);
    if (songIndex === -1) return;
    if (direction === 'up' && songIndex === 0) return;
    if (direction === 'down' && songIndex === currentSongs.length - 1) return;

    const newIndex = direction === 'up' ? songIndex - 1 : songIndex + 1;
    const newSongs = [...currentSongs];
    [newSongs[songIndex], newSongs[newIndex]] = [newSongs[newIndex], newSongs[songIndex]];

    const song_orders = newSongs.map((song, idx) => ({ id: song.id, position: idx + 1 }));
    try {
      await axios.post(`${API_URL}/api/gigs/${gigId}/setlist/reorder`, {
        set_number: currentSet,
        song_orders,
      });
      fetchSetList();
    } catch (err) {
      console.error('Error moving song:', err);
      toast.error('Failed to reorder');
    }
  };

  if (loading || !gig) {
    return <div style={{ padding: '2rem' }}>Loading set list...</div>;
  }

  const numberOfSets = gig.number_of_sets || 1;

  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const setDurationSummary = (slots) => {
    if (!slots || slots.length === 0) return null;
    const totalSecs = slots.reduce((sum, s) => {
      if (s.slot_type === 'request_block') return sum + (s.duration_minutes || 20) * 60;
      return sum + (s.duration_seconds || 180);
    }, 0);
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    const hasEstimates = slots.some(s => s.slot_type !== 'request_block' && !s.duration_seconds);
    return `${hasEstimates ? '~' : ''}${mins}:${String(secs).padStart(2, '0')}`;
  };
  const currentSetSongs = setList[currentSet] || [];
  const currentSetSongIds = currentSetSongs.filter(s => s.slot_type !== 'request_block').map(s => s.song_id);
  const venueSongIds = venueSongs.map(s => s.id);

  const availableSongs = allSongs.filter(s =>
    !currentSetSongIds.includes(s.id) &&
    (searchTerm === '' ||
      s.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.artist_original.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const selectedDurationSecs = [...selectedSongIds].reduce((sum, id) => {
    const song = allSongs.find(s => s.id === id);
    return sum + (song?.duration_seconds || 180);
  }, 0);

  const formatSelectedDuration = () => {
    const hasEstimates = [...selectedSongIds].some(id => !allSongs.find(s => s.id === id)?.duration_seconds);
    const m = Math.floor(selectedDurationSecs / 60);
    const s = selectedDurationSecs % 60;
    return `${hasEstimates ? '~' : ''}${m}:${String(s).padStart(2, '0')}`;
  };

  // ─── Mobile layout ───────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: theme.background, overflow: 'hidden', color: theme.text }}>

        {/* Header */}
        <div style={{ background: theme.surface, borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: '700', fontSize: '1rem', lineHeight: 1.2 }}>Set List Builder</div>
              <div style={{ fontSize: '0.8rem', color: theme.textSecondary, marginTop: '0.15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {gig.gig_name} @ {gig.venue_name}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, marginLeft: '0.75rem' }}>
              {venueHistory.length > 0 && (
                <button
                  onClick={() => setShowVenueHistory(!showVenueHistory)}
                  style={{
                    padding: '0.4rem 0.6rem',
                    background: showVenueHistory ? theme.primary : theme.surfaceAlt,
                    color: showVenueHistory ? theme.onPrimary : theme.textSecondary,
                    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem',
                  }}
                >
                  <ScrollText size={16} />
                </button>
              )}
              <button
                onClick={openTemplatesSheet}
                title="Set list templates"
                style={{ padding: '0.4rem 0.6rem', background: theme.surfaceAlt, color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                <Bookmark size={16} />
              </button>
              <a
                href="/band"
                target="_blank"
                rel="noreferrer"
                title="Band view"
                style={{
                  padding: '0.4rem 0.6rem',
                  background: theme.surfaceAlt, color: theme.textSecondary,
                  border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', textDecoration: 'none',
                }}
              >
                <ExternalLink size={16} />
              </a>
              <button
                onClick={() => navigate('/dashboard/gigs')}
                style={{
                  padding: '0.4rem 0.75rem',
                  background: theme.surfaceAlt, color: theme.textSecondary,
                  border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600',
                }}
              >
                ← Back
              </button>
            </div>
          </div>

          {/* Venue history — collapsible */}
          {showVenueHistory && (
            <div style={{ padding: '0.75rem 1rem', background: '#fff8e1', borderTop: '1px solid #ffe082' }}>
              {venueSongs.length > 0 && (
                <>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#856404', marginBottom: '0.4rem' }}>
                    MOST PLAYED AT {gig.venue_name.toUpperCase()}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.5rem' }}>
                    {venueSongs.slice(0, 8).map(song => (
                      <span key={song.id} style={{
                        background: theme.surface, border: '1px solid #ffc107',
                        borderRadius: '10px', padding: '0.2rem 0.6rem',
                        fontSize: '0.75rem', color: '#856404',
                      }}>
                        {song.title} ({song.times_played}x)
                      </span>
                    ))}
                  </div>
                </>
              )}
              <details>
                <summary style={{ fontSize: '0.8rem', color: '#856404', cursor: 'pointer' }}>
                  {venueHistory.length} past gigs
                </summary>
                <div style={{ marginTop: '0.4rem' }}>
                  {venueHistory.map(pastGig => (
                    <div key={pastGig.id} style={{ fontSize: '0.8rem', color: '#856404', padding: '0.3rem 0', borderBottom: '1px solid #ffe082' }}>
                      {new Date(pastGig.gig_date).toLocaleDateString('en-GB')} — {pastGig.gig_name} ({pastGig.songs_played} songs)
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', borderTop: `1px solid ${theme.border}` }}>
            {[...Array(numberOfSets)].map((_, idx) => (
              <button
                key={idx}
                onClick={() => { setCurrentSet(idx + 1); setActiveTab('setlist'); setSelectedSongIds(new Set()); }}
                style={{
                  flex: 1, padding: '0.65rem 0.5rem',
                  background: 'transparent',
                  color: activeTab === 'setlist' && currentSet === idx + 1 ? theme.primary : theme.textMuted,
                  border: 'none',
                  borderBottom: activeTab === 'setlist' && currentSet === idx + 1 ? `2px solid ${theme.primary}` : '2px solid transparent',
                  fontWeight: activeTab === 'setlist' && currentSet === idx + 1 ? '700' : '400',
                  fontSize: '0.9rem', cursor: 'pointer',
                }}
              >
                Set {idx + 1} {setList[idx + 1] ? `(${setList[idx + 1].length})` : '(0)'}
              </button>
            ))}
            <button
              onClick={() => setActiveTab('available')}
              style={{
                flex: numberOfSets > 1 ? 1 : 2,
                padding: '0.65rem 0.5rem',
                background: 'transparent',
                color: activeTab === 'available' ? theme.primary : theme.textMuted,
                border: 'none',
                borderBottom: activeTab === 'available' ? `2px solid ${theme.primary}` : '2px solid transparent',
                fontWeight: activeTab === 'available' ? '700' : '400',
                fontSize: '0.9rem', cursor: 'pointer',
              }}
            >
              + Add Songs
            </button>
          </div>
        </div>

        {/* Content area — fills remaining height */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Set List tab */}
          {activeTab === 'setlist' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* Scrollable song list */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem' }}>
                {currentSetSongs.length === 0 ? (
                  <div style={{ padding: '3rem 1rem', textAlign: 'center', color: theme.textMuted }}>
                    <div style={{ marginBottom: '0.5rem' }}><Music size={32} /></div>
                    <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Set {currentSet} is empty</div>
                    <div style={{ fontSize: '0.875rem' }}>Tap "+ Add Songs" to get started</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {currentSetSongs.map((slot, idx) => {
                      const isBlock = slot.slot_type === 'request_block';
                      const moveButtons = (
                        <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0 }}>
                          <button onClick={() => moveSong(slot.id, 'up')} disabled={idx === 0}
                            style={{ width: '32px', height: '32px', background: 'transparent', color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: idx === 0 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: idx === 0 ? 0.3 : 1 }}>
                            <ChevronUp size={15} />
                          </button>
                          <button onClick={() => moveSong(slot.id, 'down')} disabled={idx === currentSetSongs.length - 1}
                            style={{ width: '32px', height: '32px', background: 'transparent', color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: idx === currentSetSongs.length - 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: idx === currentSetSongs.length - 1 ? 0.3 : 1 }}>
                            <ChevronDown size={15} />
                          </button>
                          <button onClick={() => removeSlot(slot.id, slot.slot_type)}
                            style={{ width: '32px', height: '32px', background: 'transparent', color: '#dc3545', border: '1px solid rgba(220,53,69,0.4)', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <X size={14} />
                          </button>
                        </div>
                      );
                      if (isBlock) return (
                        <div key={slot.id} style={{
                          background: `${theme.primary}12`, borderRadius: '10px',
                          padding: '0.65rem 0.75rem', border: `1.5px dashed ${theme.primary}`,
                          display: 'flex', alignItems: 'center', gap: '0.6rem',
                        }}>
                          <Mic size={18} color={theme.primary} style={{ flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: '600', fontSize: '0.9rem', color: theme.primary }}>Audience Requests</div>
                            <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                              {[15, 20, 30, 45].map(m => (
                                <button key={m} onClick={() => updateBlockDuration(slot.id, m)}
                                  style={{ padding: '0.1rem 0.45rem', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '0.72rem', fontWeight: (slot.duration_minutes || 20) === m ? '700' : '400',
                                    background: (slot.duration_minutes || 20) === m ? theme.primary : theme.surfaceAlt,
                                    color: (slot.duration_minutes || 20) === m ? theme.onPrimary : theme.textMuted }}>
                                  {m}m
                                </button>
                              ))}
                            </div>
                          </div>
                          {moveButtons}
                        </div>
                      );
                      return (
                        <div key={slot.id} style={{
                          background: theme.surface, borderRadius: '10px',
                          padding: '0.65rem 0.75rem',
                          display: 'flex', alignItems: 'center', gap: '0.6rem',
                          boxShadow: theme.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.07)',
                        }}>
                          <div style={{ fontSize: '1rem', fontWeight: '700', color: '#bbb', minWidth: '22px', textAlign: 'right' }}>
                            {idx + 1}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: '600', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {slot.title}
                            </div>
                            <div style={{ fontSize: '0.78rem', color: theme.textMuted, marginTop: '0.1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {slot.artist_original} · {slot.genre}
                              {slot.duration_seconds && <span style={{ marginLeft: '0.3rem' }}>· {formatDuration(slot.duration_seconds)}</span>}
                            </div>
                          </div>
                          {moveButtons}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Sticky bottom bar */}
              <div style={{ padding: '0.75rem', background: theme.surface, borderTop: `1px solid ${theme.border}`, flexShrink: 0 }}>
                {currentSetSongs.length > 0 && (
                  <div style={{ fontSize: '0.8rem', color: theme.textSecondary, textAlign: 'center', marginBottom: '0.5rem' }}>
                    {setDurationSummary(currentSetSongs)}
                    {currentSetSongs.some(s => s.slot_type !== 'request_block' && !s.duration_seconds) && <span style={{ color: theme.textMuted }}> (some estimated)</span>}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => setActiveTab('available')}
                    style={{ flex: 1, padding: '0.85rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '10px', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer' }}
                  >
                    + Add Songs
                  </button>
                  <button
                    onClick={addRequestBlock}
                    style={{ padding: '0.85rem 0.75rem', background: `${theme.primary}18`, color: theme.primary, border: `1.5px dashed ${theme.primary}`, borderRadius: '10px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}
                  >
                    <Mic size={15} /> Requests
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Available Songs tab */}
          {activeTab === 'available' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* Search — fixed at top */}
              <div style={{ padding: '0.75rem', background: theme.surface, borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
                <input
                  type="text"
                  placeholder="Search songs or artists..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%', padding: '0.65rem 0.75rem', boxSizing: 'border-box',
                    border: `1px solid ${theme.border}`, borderRadius: '8px', fontSize: '0.9rem',
                    background: theme.surface, color: theme.text,
                  }}
                />
              </div>

              {/* Scrollable song list */}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {availableSongs.length === 0 ? (
                  <div style={{ padding: '3rem 1rem', textAlign: 'center', color: theme.textMuted }}>
                    {searchTerm ? 'No songs match your search.' : 'All songs are already in the set!'}
                  </div>
                ) : (
                  availableSongs.map(song => {
                    const playedBefore = venueSongIds.includes(song.id);
                    const timesPlayed = playedBefore ? venueSongs.find(s => s.id === song.id)?.times_played : 0;
                    const isSelected = selectedSongIds.has(song.id);
                    return (
                      <div
                        key={song.id}
                        onClick={() => toggleSongSelection(song.id)}
                        style={{
                          padding: '0.65rem 0.75rem',
                          borderBottom: `1px solid ${theme.border}`,
                          display: 'flex', alignItems: 'center', gap: '0.75rem',
                          background: isSelected ? `${theme.primary}15` : (playedBefore ? '#fffbea' : theme.surface),
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                          border: `2px solid ${isSelected ? theme.primary : theme.border}`,
                          background: isSelected ? theme.primary : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {isSelected && <Check size={13} color={theme.onPrimary} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '500', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {song.title}
                            {playedBefore && (
                              <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: '#856404', background: '#ffd54f', padding: '0.1rem 0.4rem', borderRadius: '6px' }}>
                                {timesPlayed}x here
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.78rem', color: theme.textMuted, marginTop: '0.1rem' }}>
                            {song.artist_original} · {song.genre}
                            {song.duration_seconds && <span> · {formatDuration(song.duration_seconds)}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Confirm bar */}
              {selectedSongIds.size > 0 && (
                <div style={{ padding: '0.75rem', background: theme.surface, borderTop: `1px solid ${theme.border}`, flexShrink: 0, display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={addSelectedSongs}
                    style={{ flex: 1, padding: '0.85rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '10px', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer' }}
                  >
                    Add {selectedSongIds.size} song{selectedSongIds.size !== 1 ? 's' : ''} · {formatSelectedDuration()}
                  </button>
                  <button
                    onClick={() => setSelectedSongIds(new Set())}
                    style={{ padding: '0.85rem 0.75rem', background: theme.surfaceAlt, color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <TemplatesSheet open={templatesOpen} onClose={closeTemplatesSheet} templates={templates} previousGig={previousGig} showSaveInput={showSaveInput} setShowSaveInput={setShowSaveInput} saveTemplateName={saveTemplateName} setSaveTemplateName={setSaveTemplateName} onSave={saveTemplate} savingTemplate={savingTemplate} onLoad={loadTemplate} loadingTemplateId={loadingTemplateId} onDelete={deleteTemplate} onCopyLastGig={copyLastGig} copyingLastGig={copyingLastGig} theme={theme} />
      </>
    );
  }

  // ─── Desktop layout ───────────────────────────────────────────────────────────
  return (
    <>
    <div style={{ background: theme.background, minHeight: '100vh', color: theme.text }}>
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ margin: '0 0 0.25rem' }}>Set List Builder</h1>
          <p style={{ color: theme.textSecondary, margin: 0 }}>{gig.gig_name} @ {gig.venue_name}</p>
          {gig.gig_date && (
            <p style={{ color: theme.textMuted, fontSize: '0.9rem', margin: '0.2rem 0 0' }}>
              {new Date(gig.gig_date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              {gig.gig_time && ` at ${gig.gig_time.substring(0, 5)}`}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {venueHistory.length > 0 && (
            <button
              onClick={() => setShowVenueHistory(!showVenueHistory)}
              style={{
                padding: '0.6rem 1rem',
                background: showVenueHistory ? theme.primary : theme.surfaceAlt,
                color: showVenueHistory ? theme.onPrimary : theme.text,
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem',
              }}
            >
              <ScrollText size={16} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} />
              {showVenueHistory ? 'Hide' : 'Show'} Venue History ({venueHistory.length} past gigs)
            </button>
          )}
          <button
            onClick={openTemplatesSheet}
            style={{ padding: '0.6rem 1rem', background: theme.surfaceAlt, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            <Bookmark size={15} /> Templates
          </button>
          <a
            href="/band"
            target="_blank"
            rel="noreferrer"
            style={{
              padding: '0.75rem 1.25rem', background: theme.surfaceAlt, color: theme.textSecondary,
              border: `1px solid ${theme.border}`, borderRadius: '8px', fontWeight: '600',
              display: 'flex', alignItems: 'center', gap: '0.4rem', textDecoration: 'none', fontSize: '0.9rem',
            }}
          >
            <ExternalLink size={15} /> Band view
          </a>
          <button
            onClick={() => navigate('/dashboard/gigs')}
            style={{ padding: '0.75rem 1.5rem', background: theme.surfaceAlt, color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
          >
            ← Back to Gigs
          </button>
        </div>
      </div>

      {/* Venue History */}
      {showVenueHistory && venueHistory.length > 0 && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Previously Played at {gig.venue_name}</h3>
          {venueSongs.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.9rem', color: '#856404', margin: '0 0 0.5rem' }}><strong>Most played songs here:</strong></p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {venueSongs.slice(0, 10).map(song => (
                  <span key={song.id} style={{ background: theme.surface, padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.85rem', color: '#856404', border: '1px solid #ffc107' }}>
                    {song.title} ({song.times_played}x)
                  </span>
                ))}
              </div>
            </div>
          )}
          <details>
            <summary style={{ cursor: 'pointer', color: '#856404', fontSize: '0.9rem' }}>View past gigs</summary>
            <div style={{ marginTop: '0.5rem' }}>
              {venueHistory.map(pastGig => (
                <div key={pastGig.id} style={{ padding: '0.5rem', borderBottom: '1px solid #ffc107', fontSize: '0.85rem', color: '#856404' }}>
                  {new Date(pastGig.gig_date).toLocaleDateString('en-GB')} — {pastGig.gig_name} ({pastGig.songs_played} songs)
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>

        {/* Available Songs */}
        <div>
          <h3 style={{ margin: '0 0 0.75rem' }}>Available Songs</h3>
          <input
            type="text"
            placeholder="🔍 Search songs..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '0.75rem', border: `2px solid ${theme.border}`, borderRadius: '8px', marginBottom: '0.75rem', boxSizing: 'border-box', background: theme.surface, color: theme.text }}
          />
          <div style={{ maxHeight: '65vh', overflowY: 'auto', border: `1px solid ${theme.border}`, borderRadius: '8px', background: theme.surfaceAlt }}>
            {availableSongs.length === 0 ? (
              <p style={{ padding: '2rem', textAlign: 'center', color: theme.textSecondary }}>
                {searchTerm ? 'No songs found' : 'All songs added to set'}
              </p>
            ) : (
              availableSongs.map(song => {
                const playedBefore = venueSongIds.includes(song.id);
                const timesPlayed = playedBefore ? venueSongs.find(s => s.id === song.id)?.times_played : 0;
                const isSelected = selectedSongIds.has(song.id);
                return (
                  <div
                    key={song.id}
                    onClick={() => toggleSongSelection(song.id)}
                    style={{
                      padding: '0.75rem 1rem', borderBottom: `1px solid ${theme.border}`,
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      background: isSelected ? `${theme.primary}15` : (playedBefore ? '#fff3cd' : theme.surface),
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{
                      width: 20, height: 20, borderRadius: '4px', flexShrink: 0,
                      border: `2px solid ${isSelected ? theme.primary : theme.border}`,
                      background: isSelected ? theme.primary : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {isSelected && <Check size={12} color={theme.onPrimary} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', fontSize: '0.95rem' }}>
                        {song.title}
                        {playedBefore && (
                          <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#856404', background: '#ffc107', padding: '0.1rem 0.5rem', borderRadius: '8px' }}>
                            Played {timesPlayed}x here
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: theme.textSecondary }}>
                        {song.artist_original} · {song.genre}
                        {song.duration_seconds && <span style={{ color: theme.textMuted }}> · {formatDuration(song.duration_seconds)}</span>}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {selectedSongIds.size > 0 && (
            <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={addSelectedSongs}
                style={{ flex: 1, padding: '0.65rem 1rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.95rem' }}
              >
                Add {selectedSongIds.size} song{selectedSongIds.size !== 1 ? 's' : ''} to Set {currentSet} · {formatSelectedDuration()}
              </button>
              <button
                onClick={() => setSelectedSongIds(new Set())}
                style={{ padding: '0.65rem 0.75rem', background: theme.surfaceAlt, color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                <X size={15} />
              </button>
            </div>
          )}
        </div>

        {/* Set List */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0 }}>Set List</h3>
            {numberOfSets > 1 && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[...Array(numberOfSets)].map((_, idx) => (
                  <button key={idx + 1} onClick={() => { setCurrentSet(idx + 1); setSelectedSongIds(new Set()); }}
                    style={{ padding: '0.5rem 1rem', background: currentSet === idx + 1 ? theme.primary : theme.surfaceAlt, color: currentSet === idx + 1 ? theme.onPrimary : theme.text, border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}>
                    Set {idx + 1}{setList[idx + 1] && ` (${setList[idx + 1].length})`}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ minHeight: '200px', maxHeight: '65vh', overflowY: 'auto', border: `2px solid ${theme.primary}`, borderRadius: '8px', background: theme.surface, padding: '0.75rem' }}>
            {currentSetSongs.length === 0 ? (
              <div style={{ padding: '3rem 1rem', textAlign: 'center', color: theme.textMuted }}>
                <p>No songs in Set {currentSet} yet.</p>
                <p style={{ fontSize: '0.9rem' }}>Add songs from the left panel.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {currentSetSongs.map((slot, idx) => {
                  const isBlock = slot.slot_type === 'request_block';
                  const moveButtons = (
                    <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                      <button onClick={() => moveSong(slot.id, 'up')} disabled={idx === 0}
                        style={{ padding: '0.4rem 0.55rem', background: 'transparent', color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '4px', cursor: idx === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', opacity: idx === 0 ? 0.3 : 1 }}><ChevronUp size={14} /></button>
                      <button onClick={() => moveSong(slot.id, 'down')} disabled={idx === currentSetSongs.length - 1}
                        style={{ padding: '0.4rem 0.55rem', background: 'transparent', color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '4px', cursor: idx === currentSetSongs.length - 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', opacity: idx === currentSetSongs.length - 1 ? 0.3 : 1 }}><ChevronDown size={14} /></button>
                      <button onClick={() => removeSlot(slot.id, slot.slot_type)}
                        style={{ padding: '0.4rem 0.55rem', background: 'transparent', color: '#dc3545', border: '1px solid rgba(220,53,69,0.4)', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><X size={14} /></button>
                    </div>
                  );
                  if (isBlock) return (
                    <div key={slot.id} style={{ padding: '0.75rem', background: `${theme.primary}10`, borderRadius: '8px', border: `1.5px dashed ${theme.primary}`, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <Mic size={18} color={theme.primary} style={{ flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '600', fontSize: '0.95rem', color: theme.primary }}>Audience Requests</div>
                        <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.3rem' }}>
                          {[15, 20, 30, 45].map(m => (
                            <button key={m} onClick={() => updateBlockDuration(slot.id, m)}
                              style={{ padding: '0.15rem 0.5rem', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: (slot.duration_minutes || 20) === m ? '700' : '400',
                                background: (slot.duration_minutes || 20) === m ? theme.primary : theme.surfaceAlt,
                                color: (slot.duration_minutes || 20) === m ? theme.onPrimary : theme.textMuted }}>
                              {m} min
                            </button>
                          ))}
                        </div>
                      </div>
                      {moveButtons}
                    </div>
                  );
                  return (
                    <div key={slot.id} style={{ padding: '0.75rem', background: theme.surfaceAlt, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: '700', color: theme.textMuted, minWidth: '28px', textAlign: 'right' }}>{idx + 1}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '500', fontSize: '0.95rem' }}>{slot.title}</div>
                        <div style={{ fontSize: '0.85rem', color: theme.textSecondary }}>
                          {slot.artist_original} · {slot.genre}
                          {slot.duration_seconds
                            ? <span style={{ marginLeft: '0.4rem', color: theme.textMuted }}>{formatDuration(slot.duration_seconds)}</span>
                            : <span style={{ marginLeft: '0.4rem', color: theme.textMuted, fontStyle: 'italic' }}>~3:00</span>}
                        </div>
                      </div>
                      {moveButtons}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
            {currentSetSongs.length > 0 && (
              <div style={{ padding: '0.6rem', background: theme.surfaceAlt, borderRadius: '6px', color: theme.primary, fontWeight: '500', fontSize: '0.9rem', flex: 1, textAlign: 'center' }}>
                Set {currentSet}: {setDurationSummary(currentSetSongs)}
                {currentSetSongs.some(s => s.slot_type !== 'request_block' && !s.duration_seconds) && <span style={{ fontWeight: '400', color: theme.textMuted, fontSize: '0.8rem' }}> (some estimated)</span>}
              </div>
            )}
            <button onClick={addRequestBlock}
              style={{ padding: '0.6rem 1rem', background: `${theme.primary}15`, color: theme.primary, border: `1.5px dashed ${theme.primary}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}>
              <Mic size={14} /> + Request Block
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>
    <TemplatesSheet open={templatesOpen} onClose={closeTemplatesSheet} templates={templates} previousGig={previousGig} showSaveInput={showSaveInput} setShowSaveInput={setShowSaveInput} saveTemplateName={saveTemplateName} setSaveTemplateName={setSaveTemplateName} onSave={saveTemplate} savingTemplate={savingTemplate} onLoad={loadTemplate} loadingTemplateId={loadingTemplateId} onDelete={deleteTemplate} onCopyLastGig={copyLastGig} copyingLastGig={copyingLastGig} theme={theme} />
    </>
  );
}

// ─── Templates bottom sheet (shared mobile + desktop) ────────────────────────
function TemplatesSheet({ open, onClose, templates, previousGig, showSaveInput, setShowSaveInput, saveTemplateName, setSaveTemplateName, onSave, savingTemplate, onLoad, loadingTemplateId, onDelete, onCopyLastGig, copyingLastGig, theme }) {
  if (!open) return null;
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: theme.surface, borderRadius: '20px 20px 0 0', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '0.85rem 1.25rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Bookmark size={18} /> Set List Templates
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, display: 'flex', alignItems: 'center' }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: '1rem 1.25rem', flex: 1 }}>
          {/* Save current set */}
          {!showSaveInput ? (
            <button onClick={() => setShowSaveInput(true)}
              style={{ width: '100%', padding: '0.75rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', fontSize: '0.9rem' }}>
              <Bookmark size={15} /> Save current set as template
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <input
                autoFocus
                value={saveTemplateName}
                onChange={e => setSaveTemplateName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') { setShowSaveInput(false); setSaveTemplateName(''); } }}
                placeholder="Template name..."
                style={{ flex: 1, padding: '0.65rem 0.75rem', border: `1px solid ${theme.border}`, borderRadius: '8px', background: theme.background, color: theme.text, fontSize: '0.9rem' }}
              />
              <button onClick={onSave} disabled={savingTemplate || !saveTemplateName.trim()}
                style={{ padding: '0.65rem 1rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', opacity: savingTemplate || !saveTemplateName.trim() ? 0.6 : 1 }}>
                {savingTemplate ? '…' : 'Save'}
              </button>
              <button onClick={() => { setShowSaveInput(false); setSaveTemplateName(''); }}
                style={{ padding: '0.65rem 0.75rem', background: theme.surfaceAlt, color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <X size={15} />
              </button>
            </div>
          )}

          {/* Copy from last gig */}
          {previousGig && (
            <button onClick={onCopyLastGig} disabled={copyingLastGig}
              style={{ width: '100%', padding: '0.7rem 1rem', background: theme.surfaceAlt, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '10px', fontWeight: '500', cursor: copyingLastGig ? 'default' : 'pointer', marginBottom: '1rem', textAlign: 'left', fontSize: '0.875rem', opacity: copyingLastGig ? 0.6 : 1 }}>
              {copyingLastGig ? 'Copying…' : `↩ Copy from last gig: ${previousGig.gig_name} (${previousGig.song_count} songs)`}
            </button>
          )}

          {/* Saved templates */}
          {templates.length === 0 ? (
            <div style={{ textAlign: 'center', color: theme.textMuted, padding: '2rem 0', fontSize: '0.875rem' }}>
              No saved templates yet. Build a set list and save it as a template to reuse across gigs.
            </div>
          ) : (
            <>
              <div style={{ fontSize: '0.7rem', fontWeight: '700', color: theme.textMuted, letterSpacing: '0.06em', marginBottom: '0.5rem' }}>SAVED TEMPLATES</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {templates.map(t => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: theme.surfaceAlt, borderRadius: '10px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>{t.name}</div>
                      <div style={{ fontSize: '0.78rem', color: theme.textMuted }}>{t.song_count} song{t.song_count !== 1 ? 's' : ''} · {new Date(t.created_at).toLocaleDateString('en-GB')}</div>
                    </div>
                    <button onClick={() => onLoad(t)} disabled={loadingTemplateId === t.id}
                      style={{ padding: '0.4rem 0.85rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem', opacity: loadingTemplateId === t.id ? 0.6 : 1, flexShrink: 0 }}>
                      {loadingTemplateId === t.id ? '…' : 'Load'}
                    </button>
                    <button onClick={() => onDelete(t.id, t.name)}
                      style={{ padding: '0.4rem 0.5rem', background: 'transparent', color: '#dc3545', border: '1px solid rgba(220,53,69,0.3)', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SetListBuilder;
